import React, { useEffect, useState, useMemo, useRef } from 'react';
import { parseISO, format } from 'date-fns';
import {
  AlertCircle,
  Edit,
  Calendar as CalendarIcon,
  Instagram,
  Facebook,
  Linkedin,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { formatDateHelper, toUtcISOString, getTimeUntil } from '../../utils/dateHelpers';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/react-datepicker.css';
import { Button, IconButton, Pill, Status, Text, Input, Drawer, Notice } from '../ui';
import useMediaQuery from '../../hooks/useMediaQuery';
import useClickOutside from '../../hooks/useClickOutside';

// Helper functions for social icons
const getSocialIcon = (platform) => {
  const iconProps = { className: 'w-6 h-6 text-white' };
  switch (platform?.toLowerCase()) {
    case 'instagram':
    case 'instagram_business':
      return <Instagram {...iconProps} />;
    case 'facebook':
      return <Facebook {...iconProps} />;
    case 'linkedin':
      return <Linkedin {...iconProps} />;
    default:
      return null;
  }
};

const getSocialIconBg = (platform) => {
  switch (platform?.toLowerCase()) {
    case 'instagram':
      return 'bg-gradient-to-r from-purple-500 to-pink-500';
    case 'instagram_business':
      return 'bg-gradient-to-r from-purple-500 to-pink-500';
    case 'facebook':
      return 'bg-blue-600';
    case 'linkedin':
      return 'bg-blue-700';
    default:
      return 'bg-gray-600';
  }
};

// Refactored to a form-only component. Props:
// - post: current post object
// - onScheduleSubmit: (selectedSocialAccounts) => void
// - onEditSubmit: (payload) => void
// - socialAccounts: array of connected accounts
// - onEditImageClick: () => void
const SchedulePost = ({
  post,
  onScheduleSubmit,
  onEditSubmit,
  socialAccounts = [],
  onEditImageClick,
  onFeedbackClick,
  onCancelPost,
  onClose,
}) => {
  const [selectedSocialAccounts, setSelectedSocialAccounts] = useState(post?.socialPlatforms || []);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [showScheduleTip, setShowScheduleTip] = useState(false);
  const [isEditingCaption, setIsEditingCaption] = useState(false);
  const [editedCaption, setEditedCaption] = useState(post?.caption || '');
  const { isMobile } = useMediaQuery();
  const rescheduleModalRef = useRef(null);

  // Use useClickOutside hook to handle closing the reschedule modal
  useClickOutside(() => {
    handleDrawerClose();
  }, rescheduleModalRef);
  const originalScheduledAt = useMemo(() => {
    return post?.scheduledAt
      ? parseISO(formatDateHelper(post.scheduledAt, "yyyy-MM-dd'T'HH:mm"))
      : new Date();
  }, [post?.scheduledAt]);
  const { handleSubmit, reset, watch, control } = useForm({
    defaultValues: {
      scheduledAt: originalScheduledAt,
    },
  });
  const currentScheduledAt = watch('scheduledAt');

  // Memoize originalScheduledAt to prevent infinite loop

  const hasDateChanged = () => {
    if (!selectedDate || !selectedTime) return false;
    const [hours, minutes] = selectedTime.split(':');
    const selectedDateTime = new Date(selectedDate);
    selectedDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return selectedDateTime.getTime() !== originalScheduledAt.getTime();
  };

  useEffect(() => {
    if (post) {
      reset({
        scheduledAt: originalScheduledAt,
      });
      setSelectedSocialAccounts(post.socialPlatforms || []);
      // Set initial date and time
      setSelectedDate(originalScheduledAt);
      setSelectedTime(format(originalScheduledAt, 'HH:mm'));
      setEditedCaption(post.caption || '');
      setIsEditingCaption(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.id, post?.scheduledAt]);

  const handleRescheduleClick = () => {
    setIsRescheduleModalOpen(true);
    // Reset to original values when opening
    setSelectedDate(originalScheduledAt);
    setSelectedTime(format(originalScheduledAt, 'HH:mm'));
  };

  const handleDrawerClose = () => {
    setIsRescheduleModalOpen(false);
    // Reset to original values when closing without submitting
    setSelectedDate(originalScheduledAt);
    setSelectedTime(format(originalScheduledAt, 'HH:mm'));
  };

  const handleSocialAccountToggle = (platform) => {
    setShowScheduleTip(false);
    setSelectedSocialAccounts((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const onEditFormSubmit = (values) => {
    // Prevent editing if post is published
    if (post?.status === 'published') {
      return;
    }

    let scheduledAt = '';
    if (selectedDate && selectedTime) {
      try {
        // Combine date and time
        const [hours, minutes] = selectedTime.split(':');
        const combinedDate = new Date(selectedDate);
        combinedDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        scheduledAt = toUtcISOString(combinedDate);
      } catch (e) {
        // surface error handling to parent
        onEditSubmit?.({ error: 'Invalid date format' });
        return;
      }
    }
    const payload = {
      scheduledAt,
    };
    onEditSubmit?.({ postId: post.id, data: payload });
    setIsRescheduleModalOpen(false);
  };

  const handleCaptionSave = () => {
    if (post?.status === 'published') return;
    onEditSubmit?.({ postId: post.id, data: { captions: editedCaption } });
    setIsEditingCaption(false);
  };

  const handleCaptionCancel = () => {
    setEditedCaption(post?.caption || '');
    setIsEditingCaption(false);
  };

  // temp fix. schedule is working both ways approve/reject
  const canSchedule = socialAccounts.length > 0 && selectedSocialAccounts.length > 0;
  const isPublished = post?.status === 'published';
  const isApprovedStatus = post?.status === 'approved' || post?.status === 'scheduled';
  const isApprovedOnAllNetworks =
    isApprovedStatus &&
    socialAccounts.length > 0 &&
    socialAccounts.every((account) => post?.socialPlatforms?.includes(account.platform));
  const isCancellable = isApprovedOnAllNetworks;

  return (
    <div className='mobile-tour-post-modal-schedule h-full py-5 flex flex-col space-y-4'>
      {/* Post Info Grid */}
      <div className='space-y-0 w-full'>
        {/* Type */}
        <div className='flex items-center justify-between py-3 w-full' style={{ borderBottom: '1px solid var(--border)' }}>
          <Status status={post?.status || 'draft'} />
          <Pill variant='secondary'>{post?.aspectRatio === '9:16' ? 'Story' : 'Feed'}</Pill>
        </div>
      </div>

      {/* Caption Edit Section - Feed posts only */}
      {post?.aspectRatio === '1:1' && (
        <div className='space-y-2 w-full'>
          <div className='flex items-center justify-between'>
                        <Text variant='caption' className='font-semibold mb-0' style={{ color: 'var(--text-primary)' }}>
              Caption
            </Text>
            {!isPublished && !isEditingCaption && (
              <IconButton
                icon={Edit}
                onClick={() => setIsEditingCaption(true)}
                variant='ghost'
                size='sm'
                tooltip='Edit caption'
              />
            )}
          </div>
          {isEditingCaption ? (
            <div className='space-y-2'>
              <textarea
                value={editedCaption}
                onChange={(e) => setEditedCaption(e.target.value)}
               className='w-full rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 transition-colors'
               style={{
                 border: '1px solid var(--border)',
                 backgroundColor: 'var(--bg-input)',
                 color: 'var(--text-body)',
               }}
               rows={5}
                placeholder='Enter caption...'
              />
              <div className='flex items-center gap-2'>
                <Button
                  variant='primary'
                  size='sm'
                  onClick={handleCaptionSave}
                  disabled={editedCaption === (post?.caption || '')}
                >
                  Save
                </Button>
                <Button variant='neutral' size='sm' onClick={handleCaptionCancel}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Text variant='caption' className='mb-0 line-clamp-3' style={{ color: 'var(--text-muted)' }}>
              {post?.caption || 'No caption'}
            </Text>
          )}
        </div>
      )}

      <div className='tour-post-modal-social-accounts space-y-3 flex-1'>
        {socialAccounts.length === 0 ? (
          <div className='flex items-center gap-2'>
            <AlertCircle className='text-amber-600' size={20} />
            <Text variant='caption' className='text-amber-800 mb-0'>
              No social accounts connected.{' '}
              <Link
                to='/socials'
                className='font-medium underline hover:text-amber-900'
                onClick={() => onClose()}
              >
                Connect accounts
              </Link>
            </Text>
          </div>
        ) : (
          <div className='space-y-3'>
            {socialAccounts.map((account) => {
              const isSelected = selectedSocialAccounts.includes(account.platform);
              return (
                <label
                  key={account.platform}
                  className={`group block ${
                    isPublished ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                  }`}
                >
                  <input
                    type='checkbox'
                    className='hidden'
                    checked={isSelected}
                    onChange={() => handleSocialAccountToggle(account.platform)}
                    disabled={isPublished}
                  />
                  <div
                    className={`flex items-center justify-between p-3 rounded-lg transition-all duration-200`}
                    style={{
                      border: `1px solid ${isSelected ? 'var(--brand)' : 'var(--border)'}`,
                      backgroundColor: isSelected ? 'var(--bg-card-alt)' : 'transparent',
                    }}
                  >
                    <div className='flex items-center gap-3'>
                      <div
                        className={`w-12 h-12 rounded-full ${getSocialIconBg(
                          account.platform
                        )} flex items-center justify-center shadow-sm`}
                      >
                        {getSocialIcon(account.platform)}
                      </div>
                      <div className='flex flex-col'>
            <Text variant='caption' className='font-semibold mb-0' style={{ color: 'var(--text-primary)' }}>
                          {account.username || account.pageName || account.displayName}
                        </Text>
                      </div>
                    </div>
                    <div
                      className='w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors'
                      style={{ borderColor: isSelected ? 'var(--brand)' : 'var(--border)' }}
                    >
                      <div
                        className='w-3 h-3 rounded-full transition-opacity'
                        style={{ backgroundColor: 'var(--brand)', opacity: isSelected ? 1 : 0 }}
                      />
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Scheduled */}
      {post?.scheduledAt && (
        // need larger font for the time
        <div className='flex flex-row items-center justify-between py-3 w-full' style={{ borderBottom: '1px solid var(--border)' }}>
          <Text variant='caption' className='mb-0' style={{ color: 'var(--text-muted)' }}>
            Publishes at
          </Text>
          <Text variant='caption' className='mb-0 font-bold' style={{ color: 'var(--text-primary)' }}>
            {formatDateHelper(post.scheduledAt, 'MMM do yyyy HH:mm')}{' '}
            <span className='font-normal' style={{ color: 'var(--text-muted)' }}>· {getTimeUntil(post.scheduledAt)}</span>
          </Text>
          {!isPublished && (
            <Button
              variant='text'
              size='sm'
              onClick={handleRescheduleClick}
              className='tour-post-modal-reschedule text-xs px-0 h-auto py-0 hover:opacity-80'
              style={{ color: 'var(--brand)' }}
            >
              Set new post time
            </Button>
          )}
        </div>
      )}
      {showScheduleTip && !canSchedule && !isPublished && (
        <Notice
          variant='warning'
          message={
            socialAccounts.length === 0
              ? 'Connect a social account to schedule this post.'
              : 'Select at least one account to publish to.'
          }
        />
      )}
      {!isCancellable && (
        <Button
          onClick={() => {
            if (!canSchedule || isPublished) {
              setShowScheduleTip(true);
              return;
            }
            onScheduleSubmit?.(selectedSocialAccounts);
          }}
          variant='primary'
          icon={CalendarIcon}
          iconPosition='left'
          className='tour-post-modal-schedule w-full'
        >
          Approve
        </Button>
      )}
      {isCancellable && onCancelPost && (
        <Button
          onClick={() => onCancelPost(selectedSocialAccounts)}
          variant='neutral'
          className='w-full'
        >
          Cancel post
        </Button>
      )}
      <Button
        onClick={onFeedbackClick}
        variant='text'
        className='tour-post-modal-feedback w-full !p-0 text-xs'
      >
        Give us your honest feedback!
      </Button>
      {!isPublished && (
        <Button
          onClick={onEditImageClick}
          variant='text'
          className='tour-post-modal-regenerate w-full !p-0 text-xs'
        >
          Don't like this post?
        </Button>
      )}

      {/* Reschedule Drawer */}
      <Drawer
        isOpen={isRescheduleModalOpen}
        onClose={handleDrawerClose}
        title='Reschedule Post'
        position='right'
        size='md'
      >
        <form
          onSubmit={handleSubmit(onEditFormSubmit)}
          className='flex flex-col h-full justify-between py-2 px-2 md:px-0 gap-4'
        >
          {/* Selected Date/Time Display */}
          {selectedDate && selectedTime && (
            <div className=' rounded-lg  m-0'>
              <Text variant='caption' className='font-semibold mb-3' style={{ color: 'var(--brand)' }}>
                Selected Date & Time
              </Text>
              <Text variant={isMobile ? 'body' : 'title'} className='m-0 font-bold' style={{ color: 'var(--text-body)' }}>
                {format(selectedDate, 'MMM do yyyy')} at {selectedTime}
              </Text>
            </div>
          )}

          {/* Calendar */}
          <div>
            <Text variant='caption' className='font-semibold mb-3 block' style={{ color: 'var(--text-primary)' }}>
              Select Date
            </Text>

            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              inline
              dateFormat='MMM do yyyy'
              placeholderText='Select date'
            />
          </div>

          {/* Time Input */}
          <div>
            <Text variant='caption' className='font-semibold mb-3 block' style={{ color: 'var(--text-primary)' }}>
              Select Time
            </Text>
            <Input
              type='time'
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              className='w-full'
            />
          </div>

          {/* Footer Buttons */}
          <div className='flex items-center gap-3 mt-auto pt-4' style={{ borderTop: '1px solid var(--border)' }}>
            <Button type='button' variant='neutral' onClick={handleDrawerClose} className='flex-1'>
              Cancel
            </Button>
            <Button type='submit' variant='primary' className='flex-1' disabled={!hasDateChanged()}>
              Reschedule
            </Button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default SchedulePost;
